import { createFilterOptions, FilterOptionsState, TextField } from '@mui/material'
import { getGridStringOperators, GridFilterInputValueProps, useGridApiContext } from '@mui/x-data-grid'
import isNil from 'lodash/isNil'
import { ComponentType, ReactElement, SyntheticEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseAutocomplete } from '@omnea-app/omui/BaseAutocomplete'

type AutoCompleteInputFilterProps = Omit<GridFilterInputValueProps, 'apiRef'> & {
  options: string[]
  filterOptions?: (options: string[], state: FilterOptionsState<string>) => string[]
  loading?: boolean
  onSearchChange?: (search: string) => void
}

type RowValuesAutocompleteFilterProps = GridFilterInputValueProps & {
  valueProp?: string // Optional prop to use as value for options
}

type ListAutocompleteFilterProps = GridFilterInputValueProps & {
  options: string[]
}

type SearchAutocompleteFilterProps = ListAutocompleteFilterProps & {
  loading: boolean
  onSearchChange: (search: string) => void
}

const AutoCompleteInputFilter = ({
  item,
  applyValue,
  options,
  filterOptions,
  loading,
  onSearchChange,
}: AutoCompleteInputFilterProps) => {
  const { t } = useTranslation('shared')
  const [inputValue, setInputValue] = useState(item.value ?? '')

  const handleInputChange = (_: SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue)
    onSearchChange?.(newInputValue)
  }

  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setInputValue(newValue)
    applyValue({ ...item, value: newValue })
  }

  const handleBlur = () => {
    if (inputValue === item.value) return
    applyValue({ ...item, value: inputValue })
  }

  return (
    <BaseAutocomplete
      freeSolo
      autoHighlight
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disableCloseOnSelect
      disableClearable
      onChange={handleChange}
      onBlur={handleBlur}
      options={options}
      filterOptions={filterOptions}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label={t('table-filters.filter-value-placeholder')}
          placeholder={t('table-filters.filter-value')}
          slotProps={{
            input: { ...params.InputProps, sx: { border: 'border.brand.strong' } },
            inputLabel: { shrink: true },
          }}
        />
      )}
    />
  )
}

const RowValuesAutocompleteFilter = ({ valueProp, ...props }: RowValuesAutocompleteFilterProps) => {
  const apiRef = useGridApiContext()
  const { field } = props.item

  const options = useMemo(() => {
    const rowValues = [...apiRef.current.getRowModels()].map((values) => values[1][field]).filter((value) => !isNil(value))

    // if rowValues are arrays, flatten them, then check for valueProp
    const options = rowValues
      .flatMap((row) =>
        Array.isArray(row) ? row.map((item) => (!!valueProp ? item[valueProp] : item)) : !!valueProp ? row[valueProp] : row
      )
      .filter((value) => !!value)

    return [...new Set(options)]
  }, [field, apiRef, valueProp])

  return <AutoCompleteInputFilter {...props} options={options} />
}

const MAX_RENDERED_OPTIONS = 200
const limitListOptions = createFilterOptions<string>({ limit: MAX_RENDERED_OPTIONS })

export const ListAutocompleteFilter = (props: ListAutocompleteFilterProps) => (
  <AutoCompleteInputFilter {...props} filterOptions={limitListOptions} />
)

const keepEveryOption = (options: string[]) => options

export const SearchAutocompleteFilter = (props: SearchAutocompleteFilterProps) => (
  <AutoCompleteInputFilter {...props} filterOptions={keepEveryOption} />
)

const autocompletableOperators = new Set(['contains', 'doesNotContain', 'equals', 'doesNotEqual'])

// MUI erases the filter value when the operator changes to one with a different InputComponent, so each operator
// gets its own.
const swapAutocompleteInput = (renderInput: (props: GridFilterInputValueProps) => ReactElement) =>
  getGridStringOperators().map((operator) =>
    autocompletableOperators.has(operator.value)
      ? { ...operator, InputComponent: (props: GridFilterInputValueProps) => renderInput(props) }
      : operator
  )

// Suggests values from the rows currently loaded, so on a server-paginated grid it only knows the current page;
// use getGridListAutocompleteFilterOperator there.
export const getGridAutocompleteFilterOperator = (valueProp?: string) =>
  swapAutocompleteInput((props) => <RowValuesAutocompleteFilter {...props} valueProp={valueProp} />)

// Call once at module scope: every call mints new InputComponents, and a new component type remounts the input,
// dropping what the user has typed.
export const getGridListAutocompleteFilterOperator = (SourceInput: ComponentType<GridFilterInputValueProps>) =>
  swapAutocompleteInput((props) => <SourceInput {...props} />)
